/* eslint-disable prettier/prettier */
import { Typography } from '@uhc-tempo/components';
import DisclaimerMessageUtil from './DisclaimerMessageUtil';
import { useContext, useState } from 'react';
import { FormContext } from '../../../context/Form.context';
import { Grid } from '@mui/material';
import { UMR_SOURCECODE } from '../../../constants';
/**
 * Fucntion that determines the age restriction message based on the minimum and maximum age
 * @param {*} minimumAge minimum age for restriction in days
 * @param {*} maximumAge maximum age for restriction in days
 * @returns A string representing age restriction message
 */
const getAgeRestrictionMessage = (minimumAge, maximumAge) => {
    //convert days to months
    const minAgeInMonths = Math.floor(minimumAge / 30);
    const maxAgeInMonths = maximumAge < 100000 ? Math.floor(maximumAge / 30) : maximumAge;
    if (minAgeInMonths > 0 && maxAgeInMonths == 100000) {
        let duration;
        if (minAgeInMonths < 36) {
            duration = 'months';
            return `This service has a minimum age requirement. It won't be fully covered until you are ${minAgeInMonths} ${duration} old.`;
        } else {
            duration = 'years';
            return `This service has a minimum age requirement. It won't be fully covered until you are ${Math.floor(
                minimumAge / 365
            )} ${duration} old.`;
        }
    } else if (minAgeInMonths > 0 && maxAgeInMonths < 100000) {
        let duration;
        if (maxAgeInMonths < 36) {
            duration = 'months';
            return `This service has a minimum age requirement. It won't be fully covered unless you are ${minAgeInMonths} - ${maxAgeInMonths} ${duration} old.`;
        } else {
            duration = 'years';
            return `This service has a minimum age requirement. It won't be fully covered unless you are ${Math.floor(
                minimumAge / 365
            )} - ${Math.floor(maximumAge / 365)} ${duration} old.`;
        }
    } else if (minAgeInMonths == 0) {
        let duration;
        if (maxAgeInMonths <= 3) {
            duration = 'days';
            return `This service is covered up to ${maximumAge} ${duration} after birth.`;
        } else if (maxAgeInMonths < 36) {
            duration = 'months';
            return `This service is covered up to ${maxAgeInMonths} ${duration} after birth.`;
        } else {
            duration = 'years';
            return `This service is covered up to ${Math.floor(
                maximumAge / 365
            )} ${duration} after birth.`;
        }
    } else {
        throw new Error(
            `Unhandled Age restrictions message conditions. minimumAge:  ${minimumAge}, maximumAge: ${maximumAge}`
        );
    }
};

/**
 * Function that displays the age restriction disclaimer message to display in multiple estimates page.
 * @returns A disclaimer message component that displays the age restriction disclaimer message
 */
export function AgeRestrictionDisclaimer() {
    const { formValues } = useContext(FormContext);
    const { selectedService } = formValues;
    const { ageRestriction } = selectedService?.value?.fullCarePath;
    const [showAgeRestrictionDisclaimer, setAgeRestrictionDisclaimer] = useState(
        ageRestriction || false
    );
    const dataTestID = 'ageRestrictionDisclaimer';
    const messageKey = 'Age requirement';
    const isWarningDisclaimer = false;
    const memberDetails = useContext(FormContext);
    if (
        showAgeRestrictionDisclaimer &&
        memberDetails?.memberDetails?.sourceCode !== UMR_SOURCECODE
    ) {
        let messageValue;
        try {
            messageValue = getAgeRestrictionMessage(
                ageRestriction.minimumAge,
                ageRestriction.maximumAge
            );
        } catch (error) {
            console.error(error);
            return null;
        }

        return (
            <DisclaimerMessageUtil
                handleCloseIconClick={() => {
                    setAgeRestrictionDisclaimer(false);
                }}
                messageKey={messageKey}
                messageValue={messageValue}
                dataTestID={dataTestID}
                isWarningDisclaimer={isWarningDisclaimer}
            />
        );
    } else return null;
}

/**
 * Function that displays the age restriction message to display in single estimates page below you pay amount.
 * @returns A paragraph message component that displays the age restriction disclaimer message
 */
export function AgeRestrictionMessage() {
    const { formValues } = useContext(FormContext);
    const { selectedService } = formValues;
    const { ageRestriction } = selectedService?.value?.fullCarePath;
    const memberDetails = useContext(FormContext);
    const showAgeRestrictionDisclaimer = ageRestriction || false;
    if (
        showAgeRestrictionDisclaimer &&
        memberDetails?.memberDetails?.sourceCode !== UMR_SOURCECODE
    ) {
        let message;
        try {
            message = getAgeRestrictionMessage(
                ageRestriction.minimumAge,
                ageRestriction.maximumAge
            );
        } catch (error) {
            console.error(error);
            return null;
        }

        return (
            <Grid item xs={12}>
                <Typography.Paragraph style={{ color: '#002677' }}>
                    <sup>*</sup>
                    {message}
                </Typography.Paragraph>
            </Grid>
        );
    } else return null;
}
